/**
 * Admin Web Push subscription client. iter192.
 * Wraps the browser's Notification + PushManager APIs and syncs the
 * resulting subscription with the backend.
 */
import { apiClient } from "./api";

function urlB64ToUint8Array(b64) {
  const padding = "=".repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

export function pushSupported() {
  return (
    typeof window !== "undefined"
    && "serviceWorker" in navigator
    && "PushManager" in window
    && "Notification" in window
  );
}

export function currentPermission() {
  if (typeof Notification === "undefined") return "unsupported";
  return Notification.permission; // "granted" | "denied" | "default"
}

async function getRegistration() {
  // Prefer the already-registered SW; fall back to registering ours.
  const reg = await navigator.serviceWorker.getRegistration();
  return reg || (await navigator.serviceWorker.register("/sw.js"));
}

export async function getBrowserSubscription() {
  if (!pushSupported()) return null;
  const reg = await getRegistration();
  return await reg.pushManager.getSubscription();
}

export async function subscribeAdmin() {
  if (!pushSupported()) throw new Error("Web Push not supported in this browser");
  const perm = await Notification.requestPermission();
  if (perm !== "granted") throw new Error("Permission denied");

  const { data } = await apiClient.get("/admin/push/vapid-key");
  const publicKey = data?.public_key;
  if (!publicKey) throw new Error("VAPID key missing on server");

  const reg = await getRegistration();
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlB64ToUint8Array(publicKey),
    });
  }
  // Send to backend (JSON-safe form).
  const j = sub.toJSON();
  await apiClient.post("/admin/push/subscribe", {
    endpoint: j.endpoint,
    keys: { p256dh: j.keys.p256dh, auth: j.keys.auth },
  });
  return true;
}

export async function unsubscribeAdmin() {
  if (!pushSupported()) return;
  const reg = await getRegistration();
  const sub = await reg.pushManager.getSubscription();
  const endpoint = sub?.endpoint;
  if (sub) await sub.unsubscribe();
  await apiClient.delete("/admin/push/unsubscribe", { params: endpoint ? { endpoint } : {} });
}

export async function fetchPushStatus() {
  const { data } = await apiClient.get("/admin/push/status");
  return data;
}

export async function updatePushPrefs(prefs) {
  const { data } = await apiClient.patch("/admin/push/prefs", prefs);
  return data;
}

export async function sendTestPush() {
  const { data } = await apiClient.post("/admin/push/test");
  return data;
}
